import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCurriculum } from "@/context/CurriculumContext";
import { toast } from "sonner";
import DocHeader from "@/components/DocHeader";
import { SUBJECT_META } from "@/lib/scale";
import { ArrowLeft, Printer, Loader2, Sprout, Sparkles, Save } from "lucide-react";

const PROGRESS = [
  { key: "not_achieved", label: "Not Achieved" },
  { key: "acquiring", label: "Acquiring" },
  { key: "becoming_fluent", label: "Becoming Fluent" },
  { key: "achieved", label: "Achieved" },
];

export default function TargetSheet() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const curriculum = useCurriculum();
  const [student, setStudent] = useState(null);
  const [targets, setTargets] = useState([]);
  const [reviewDate, setReviewDate] = useState("");
  const [signDate, setSignDate] = useState(new Date().toISOString().slice(0, 10));
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/students/${studentId}`).then((r) => {
      setStudent(r.data);
      const prio = (r.data.targets || []).filter((t) => t.priority);
      setTargets(prio.map((t) => ({
        ...t,
        progress: t.progress || "not_achieved",
        strategies: t.strategies || "",
        resources: t.resources || "",
        personnel: t.personnel || "",
      })));
    }).catch(() => navigate("/"));
  }, [studentId, navigate]);
  useEffect(() => { load(); }, [load]);

  const upd = (idx, field, val) =>
    setTargets((arr) => arr.map((t, i) => (i === idx ? { ...t, [field]: val } : t)));

  const fillAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/suggest/target-sheet/${studentId}`);
      setTargets((arr) => arr.map((t) => {
        const d = data.details[t.id];
        return d ? { ...t, strategies: d.strategies, resources: d.resources, personnel: d.personnel } : t;
      }));
      toast.success("AI filled the support plan — review and edit as needed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAiLoading(false);
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await Promise.all(targets.map((t) =>
        api.patch(`/targets/${t.id}`, {
          progress: t.progress, strategies: t.strategies, resources: t.resources, personnel: t.personnel,
        })
      ));
      toast.success("Sheet saved");
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!student || !curriculum)
    return <div className="min-h-screen flex justify-center items-center text-slate-300"><Loader2 className="animate-spin" /></div>;

  const subjects = curriculum.subjects;

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-8 px-4">
      {/* Toolbar */}
      <div className="max-w-3xl mx-auto flex items-center justify-between gap-3 mb-6 no-print flex-wrap">
        <button
          data-testid="sheet-back-btn"
          onClick={() => navigate(`/student/${studentId}`)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition"
        >
          <ArrowLeft size={16} /> Back to profile
        </button>
        <div className="flex items-center gap-2">
          <button
            data-testid="sheet-ai-fill-btn"
            onClick={fillAi}
            disabled={aiLoading || targets.length === 0}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#81B29A,#5d8a73)" }}
          >
            {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {aiLoading ? "Filling…" : "Fill with AI"}
          </button>
          <button
            data-testid="sheet-save-btn"
            onClick={saveAll}
            disabled={saving || targets.length === 0}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] bg-white border border-slate-200 text-slate-700 hover:border-[#81B29A] transition disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
          </button>
          <button
            data-testid="sheet-print-btn"
            onClick={() => window.print()}
            className="bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-5 py-2 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <Printer size={17} /> Print
          </button>
        </div>
      </div>

      {/* Sheet */}
      <div className="print-area max-w-3xl mx-auto bg-white rounded-2xl shadow-[0_4px_24px_rgba(42,59,71,0.06)] p-8 sm:p-10">
        <DocHeader printOnly subtitle="Priority Learning Targets" />
        <div className="flex items-center justify-between border-b border-slate-100 pb-5">
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-xl bg-[#81B29A] flex items-center justify-center text-white"><Sprout size={18} /></span>
            <span className="font-['Outfit'] font-bold text-lg text-slate-800">BloomTrack</span>
          </div>
          <p className="text-sm text-slate-400">{student.class_level}{student.class_name ? ` · ${student.class_name}` : ""}</p>
        </div>

        <h1 className="font-['Outfit'] text-3xl font-bold text-slate-800 mt-6">Priority Learning Targets</h1>
        <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
          <Info label="Name" value={student.name} />
          <Info label="Class" value={student.class_name} />
        </div>

        {targets.length === 0 ? (
          <p className="text-sm text-slate-400 mt-8">No priority targets set yet. Add targets on the student profile first.</p>
        ) : (
          <div className="mt-6 space-y-6">
            {targets.map((t, idx) => {
              const meta = SUBJECT_META[t.subject];
              const dom = subjects[t.subject]?.domains.find((d) => d.key === t.domain);
              return (
                <div key={t.id} data-testid={`sheet-target-${idx}`} className="rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h2 className="font-['Outfit'] text-lg font-bold" style={{ color: meta?.tint }}>
                      {meta?.name || "Target"}{dom ? ` — ${dom.name}` : ""}
                    </h2>
                    <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                      {t.target_level ? `Target level ${t.target_level}` : ""}
                    </span>
                  </div>

                  <SheetRow label="Target">
                    <p className="text-slate-800 text-sm">{t.text}</p>
                  </SheetRow>

                  <SheetRow label="Target Progress">
                    <div className="flex flex-wrap gap-2 mt-1">
                      {PROGRESS.map((p) => {
                        const active = t.progress === p.key;
                        return (
                          <button
                            key={p.key}
                            data-testid={`sheet-progress-${idx}-${p.key}`}
                            onClick={() => upd(idx, "progress", p.key)}
                            className={`inline-flex items-center gap-1.5 text-sm rounded-lg border px-3 py-1.5 transition ${
                              active ? "border-[#4A5D6B] bg-[#4A5D6B] text-white" : "border-slate-300 text-slate-600 hover:border-slate-400"
                            }`}
                          >
                            <span className={`h-3.5 w-3.5 rounded-[3px] border flex items-center justify-center ${active ? "bg-white border-white" : "border-slate-400"}`}>
                              {active && <span className="h-2 w-2 rounded-[1px] bg-[#4A5D6B]" />}
                            </span>
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                  </SheetRow>

                  <SheetField label="Personnel involved" testId={`sheet-personnel-${idx}`} value={t.personnel} onChange={(v) => upd(idx, "personnel", v)} rows={1} placeholder="e.g. Class Teacher, SET, SNA, Parents" />
                  <SheetField label="Strategies to help student achieve targets" testId={`sheet-strategies-${idx}`} value={t.strategies} onChange={(v) => upd(idx, "strategies", v)} rows={3} placeholder="Teaching strategies and activities…" />
                  <SheetField label="Resources Required" testId={`sheet-resources-${idx}`} value={t.resources} onChange={(v) => upd(idx, "resources", v)} rows={2} placeholder="Materials and resources…" />
                </div>
              );
            })}
          </div>
        )}

        {/* Sign-off */}
        <div className="mt-10 pt-6 border-t border-slate-200 grid sm:grid-cols-2 gap-x-8 gap-y-8">
          <SignLine label="Signature of Parent(s)/Guardian(s)" />
          <SignLine label="Signature of Class Teacher" />
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-1">Date</p>
            <input
              data-testid="sheet-date-input"
              type="date"
              value={signDate}
              onChange={(e) => setSignDate(e.target.value)}
              className="w-full border-b border-slate-300 pb-1.5 outline-none text-slate-700 bg-transparent"
            />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-1">Agreed Review Date</p>
            <input
              data-testid="sheet-review-date-input"
              type="date"
              value={reviewDate}
              onChange={(e) => setReviewDate(e.target.value)}
              className="w-full border-b border-slate-300 pb-1.5 outline-none text-slate-700 bg-transparent"
            />
          </div>
        </div>

        <p className="text-xs text-slate-400 mt-10 pt-5 border-t border-slate-100 text-center">
          BloomTrack · Priority Learning Targets · Aligned to the Irish primary curriculum
        </p>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</p>
      <p className="font-semibold text-slate-800 font-['Outfit']">{value || "—"}</p>
    </div>
  );
}

function SheetRow({ label, children }) {
  return (
    <div className="mt-3">
      <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SheetField({ label, value, onChange, rows, placeholder, testId }) {
  return (
    <div className="mt-3">
      <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">{label}</p>
      <textarea
        data-testid={testId}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-[#F9F9F7] border border-slate-100 rounded-lg px-3 py-2 text-sm text-slate-700 outline-none focus:border-[#81B29A] focus:bg-white resize-none transition"
      />
    </div>
  );
}

function SignLine({ label }) {
  return (
    <div>
      <div className="h-8 border-b border-slate-400" />
      <p className="text-xs text-slate-500 mt-1.5">{label}</p>
    </div>
  );
}
